<template>
    <div
        :class="[
            fixed ? 'fixed top-0 left-0 w-full z-30' : 'relative w-full',
            'transition-all duration-200 ease-in-out',
            square ? 'rounded-none' : 'rounded-b-2xl',
            colorClasses,
            shadow || shadowActive ? 'shadow-lg' : '',
            hidden ? '-translate-y-full' : 'translate-y-0',
            paddingScroll && !paddingScrollActive ? 'py-3' : ''
        ]"
    >
        <div
            class="relative flex items-center justify-between w-full box-border px-1 md:px-3 py-1.5"
        >
            <div
                v-show="!(leftCollapsed && collapsedForced)"
                ref="left"
                class="flex items-center justify-start"
            >
                <slot name="left"></slot>
            </div>

            <div
                v-show="!(centerCollapsed && collapsedForced)"
                ref="center"
                class="flex items-center justify-center flex-wrap flex-1"
            >
                <slot></slot>
            </div>

            <div
                v-show="!(rightCollapsed && collapsedForced)"
                ref="right"
                class="flex items-center justify-end"
            >
                <slot name="right"></slot>
            </div>

            <div
                v-if="!notLine"
                :class="[
                    'absolute bg-current',
                    lineNotTransition ? '' : 'transition-all duration-200'
                ]"
                :style="{
                    left: `${leftLine}px`,
                    width: `${widthLine}px`,
                    top: `${topLine}px`,
                    height: '3px'
                }"
            ></div>
        </div>
    </div>
</template>

<script>
export default {
    name: "CustomNavbar",
    provide() {
        return {
            customNavbar: {
                setModel: this.setModel,
                setLeftLine: this.setLeftLine,
                setWidthLine: this.setWidthLine,
                setTopLine: this.setTopLine
            }
        };
    },
    props: {
        value: { type: [String, Number, Boolean], default: null },
        fixed: { type: Boolean, default: false },
        shadow: { type: Boolean, default: false },
        shadowScroll: { type: Boolean, default: false },
        hideScroll: { type: Boolean, default: false },
        textWhite: { type: Boolean, default: false },
        square: { type: Boolean, default: false },
        paddingScroll: { type: Boolean, default: false },
        notLine: { type: Boolean, default: false },
        leftCollapsed: { type: Boolean, default: false },
        centerCollapsed: { type: Boolean, default: false },
        rightCollapsed: { type: Boolean, default: false },
        targetScroll: { type: String, default: null },
        color: { type: String, default: null },
        primary: { type: Boolean, default: false },
        success: { type: Boolean, default: false },
        warn: { type: Boolean, default: false },
        danger: { type: Boolean, default: false }
    },
    data: () => ({
        leftLine: 0,
        widthLine: 0,
        topLine: 0,
        scrollTop: 0,
        collapsedWidth: 0,
        hidden: false,
        shadowActive: false,
        paddingScrollActive: false,
        lineNotTransition: false,
        collapsedForced: false
    }),
    computed: {
        colorClasses() {
            const brandStackColors = {
                "brand-blue": "bg-brand-blue text-white",
                "brand-red": "bg-brand-red text-white",
                "brand-magenta": "bg-brand-magenta text-white",
                "brand-purple": "bg-brand-purple text-white",
                "stack-primary": "bg-theme-primary text-white",
                "stack-secondary": "bg-stack-secondary text-white",
                "stack-emphasis": "bg-stack-emphasis text-white",
                "stack-details": "bg-stack-details text-white"
            };

            if (this.danger) return "bg-theme-danger text-white";
            if (this.success) return "bg-theme-success text-white";
            if (this.warn) return "bg-theme-warning text-white";
            if (this.primary) return "bg-theme-primary text-theme-inverse";
            if (this.color && brandStackColors[this.color]) {
                return brandStackColors[this.color];
            }

            return this.textWhite
                ? "bg-theme-card text-white"
                : "bg-theme-card text-theme-base";
        }
    },
    watch: {
        hideScroll() {
            this.refreshScrollListener();
        },
        paddingScroll() {
            this.refreshScrollListener();
        },
        shadowScroll() {
            this.refreshScrollListener();
        }
    },
    mounted() {
        setTimeout(() => {
            const left = this.$refs.left;
            const center = this.$refs.center;
            const right = this.$refs.right;
            this.collapsedWidth =
                (left ? left.offsetWidth : 0) +
                (center ? center.offsetWidth : 0) +
                (right ? right.offsetWidth : 0) +
                150;
            this.handleResize();
        }, 150);

        this.refreshScrollListener();
        window.addEventListener("resize", this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize);
        this.removeScrollListener();
    },
    methods: {
        setModel(id) {
            this.$emit("input", id);
        },
        setLeftLine(left, transition = true) {
            this.lineNotTransition = !transition;
            this.$nextTick(() => {
                this.leftLine = left;
            });
        },
        setWidthLine(width) {
            this.$nextTick(() => {
                this.widthLine = width;
            });
        },
        setTopLine(top) {
            this.$nextTick(() => {
                this.topLine = top;
            });
        },
        scrollTarget() {
            return this.targetScroll
                ? document.querySelector(this.targetScroll)
                : window;
        },
        handleScroll() {
            const target = this.scrollTarget();
            if (!target) return;

            const scrollTop = this.targetScroll
                ? target.scrollTop
                : window.pageYOffset;

            if (this.hideScroll) {
                this.hidden = Math.sign(scrollTop - this.scrollTop) === 1;
            }
            if (this.shadowScroll) {
                this.shadowActive = scrollTop > 0;
            }
            if (this.paddingScroll) {
                this.paddingScrollActive = scrollTop > 0;
            }
            this.scrollTop = scrollTop;
        },
        refreshScrollListener() {
            this.removeScrollListener();
            if (this.hideScroll || this.shadowScroll || this.paddingScroll) {
                const target = this.scrollTarget();
                if (target)
                    target.addEventListener("scroll", this.handleScroll);
            }
        },
        removeScrollListener() {
            const target = this.scrollTarget();
            if (target) target.removeEventListener("scroll", this.handleScroll);
        },
        handleResize() {
            const active = this.$el.querySelector(
                ".custom-navbar__item.active"
            );
            if (active) {
                this.setLeftLine(active.offsetLeft, false);
                this.setTopLine(active.offsetTop + active.offsetHeight);
            } else {
                this.widthLine = 0;
            }

            const isCollapsible =
                this.leftCollapsed ||
                this.centerCollapsed ||
                this.rightCollapsed;
            this.collapsedForced =
                isCollapsible && this.$el.offsetWidth < this.collapsedWidth;

            this.$emit("collapsed", this.collapsedForced);
        }
    }
};
</script>
